"use client"

import { useCallback, useState } from "react"
import { Eye, MoreHorizontal, Share2, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { FileIcon } from "@/components/ui/file-icon"
import { FilePreviewDialog } from "@/components/file-preview-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { CloudFileItem } from '@/api/cloud'
import { formatFileSize } from '@/api/cloud'

interface CloudFileListProps {
  files: CloudFileItem[]
  selectedFiles: number[]
  onSelectChange: (ids: number[]) => void
  onShare?: (file: CloudFileItem) => void
  onDelete?: (file: CloudFileItem) => void
  loading?: boolean
}

export function CloudFileList({ 
  files, 
  selectedFiles, 
  onSelectChange, 
  onShare, 
  onDelete, 
  loading 
}: CloudFileListProps) {
  const [previewFile, setPreviewFile] = useState<CloudFileItem | null>(null)

  const handleSelect = useCallback((id: number, checked: boolean) => {
    if (checked) {
      onSelectChange([...selectedFiles, id])
    } else {
      onSelectChange(selectedFiles.filter((fileId) => fileId !== id))
    }
  }, [selectedFiles, onSelectChange])

  // 处理行点击，切换选中状态
  const handleRowClick = useCallback((fileId: number, event: React.MouseEvent) => {
    // 如果点击的是复选框、下拉菜单或按钮，不触发行点击
    const target = event.target as HTMLElement
    if (target.closest('[data-checkbox]') || 
        target.closest('[data-dropdown-menu]') ||
        target.closest('button') ||
        target.closest('[role="menuitem"]')) {
      return
    }
    
    const isSelected = selectedFiles.includes(fileId)
    handleSelect(fileId, !isSelected)
  }, [selectedFiles, handleSelect])

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 10 }).map((_, index) => (
          <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse h-16" />
        ))}
      </div>
    )
  }

  if (!loading && files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center bg-white border border-dashed border-gray-200 rounded-2xl">
        <p className="text-lg font-medium text-gray-900 mb-2">暂时没有文件</p>
        <p className="text-sm text-gray-500">上传或分享文件后，会自动出现在这里</p>
      </div>
    )
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="divide-y divide-gray-100">
          {files.map((file) => (
            <div
              key={file.id}
              onClick={(e) => handleRowClick(file.id, e)}
              className={`grid grid-cols-[auto_auto_1fr_auto_auto] gap-4 px-5 py-3 hover:bg-slate-50/80 transition-colors items-center cursor-pointer ${
                selectedFiles.includes(file.id) ? "bg-blue-50/40" : ""
              }`}
            >
              {/* 复选框 */}
              <div 
                className="flex items-center"
                data-checkbox
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={selectedFiles.includes(file.id)}
                  onCheckedChange={(checked) => handleSelect(file.id, checked === true)}
                />
              </div>

              {/* 文件图标 */}
              <div className="flex items-center justify-center w-10 h-10 bg-slate-100/70 border border-slate-200/50 rounded-lg">
                <FileIcon
                  fileName={file.displayName || file.originalName}
                  fileType={file.fileType}
                  contentType={file.contentType}
                  size="md"
                />
              </div>

              {/* 文件信息 */}
              <div className="min-w-0">
                <p 
                  className="text-sm font-medium text-gray-900 truncate hover:text-blue-600 transition-colors"
                  title={file.displayName || file.originalName}
                  onClick={(e) => {
                    e.stopPropagation()
                    setPreviewFile(file)
                  }}
                >
                  {file.displayName || file.originalName}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatFileSize(file.fileSize)} · <span className="uppercase text-[10px] font-mono">{file.fileType}</span>
                </p>
              </div>

              {/* 创建时间 */}
              <div className="text-xs text-gray-500 hidden sm:block">
                {new Date(file.createdAt).toLocaleString('zh-CN', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>

              {/* 操作按钮 */}
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-400 hover:text-blue-600"
                  onClick={(e) => {
                    e.stopPropagation()
                    setPreviewFile(file)
                  }}
                  title="预览"
                >
                  <Eye className="w-4 h-4" />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-gray-900"
                      onClick={(e) => e.stopPropagation()}
                      data-dropdown-menu
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-36">
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation()
                        setPreviewFile(file)
                      }}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      预览
                    </DropdownMenuItem>
                    {onShare && (
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation()
                          onShare(file)
                        }}
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        分享
                      </DropdownMenuItem>
                    )}
                    {onDelete && (
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(file)
                        }}
                        className="text-red-600 focus:text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        删除
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      </div>

      <FilePreviewDialog
        file={previewFile}
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </>
  )
}

